/* ============================================================
   Divan Configurator
   Bypasses Shopify's 3-option / 100-variant cap by composing a
   "bundle" at add-to-cart time:
     POST /cart/add.js { items: [main, ...addOns], all sharing _bundle_id }

   Progressive disclosure:
     Step 1 — Size + Removal always visible.
     Step 2 — Storage/Fabric/Headboard reveal after size picked.
     Step 3 — Headboard Type reveals after headboard picked.
   ============================================================ */
(function () {
  class DivanConfigurator extends HTMLElement {
    constructor() {
      super();
      this.state = {
        sizeVariantId: null,
        sizeSelected: false,
        headboardSelected: false,
        selections: {
          storage: null,
          headboard_type: null,
          removal: null,
          fabric: null,
          headboard: null,
        },
      };
    }

    connectedCallback() {
      const dataEl = this.querySelector('[data-dc-data]');
      if (!dataEl) { console.error('[DivanConfigurator] missing data tag'); return; }
      try {
        this.data = JSON.parse(dataEl.textContent);
      } catch (e) {
        console.error('[DivanConfigurator] bad JSON:', e);
        return;
      }

      // Panel + size-chart modal live at <body> level (see
      // snippets/divan-configurator-overlays.liquid), so look them up
      // from `document`, not from `this`.
      this.panelEl    = document.querySelector('[data-dc-panel]');
      this.panelBody  = document.querySelector('[data-dc-panel-body]');
      this.panelTitle = document.querySelector('[data-dc-panel-title]');

      if (!this.panelEl) {
        console.warn('[DivanConfigurator] panel element not found — was divan-configurator-overlays rendered in theme.liquid?');
      }

      this.bindEvents();
      this.refreshTotal();
    }

    bindEvents() {
      // Use document-level delegation so we handle clicks on the panel
      // (which lives outside the custom element).
      document.addEventListener('click', (e) => {
        // Row open
        const openBtn = e.target.closest('[data-dc-open]');
        if (openBtn && this.contains(openBtn)) {
          e.preventDefault();
          const key = openBtn.dataset.dcOpen;
          if (key !== 'size' && key !== 'removal' && !this.state.sizeSelected) {
            this.openPanel('size');
            return;
          }
          if (key === 'headboard_type' && !this.state.headboardSelected) {
            this.openPanel('headboard');
            return;
          }
          this.openPanel(key);
          return;
        }

        // Close panel
        const closeBtn = e.target.closest('[data-dc-close]');
        if (closeBtn && this.panelEl && this.panelEl.contains(closeBtn)) {
          e.preventDefault();
          this.closePanel();
          return;
        }

        // ATC
        const atc = e.target.closest('[data-dc-atc]');
        if (atc && this.contains(atc)) {
          e.preventDefault();
          if (!this.state.sizeSelected) { this.openPanel('size'); return; }
          this.submit();
          return;
        }

        // Pick option inside panel (panel is outside custom element)
        const pickBtn = e.target.closest('[data-dc-pick]');
        if (pickBtn && this.panelEl && this.panelEl.contains(pickBtn)) {
          e.preventDefault();
          try {
            const payload = JSON.parse(pickBtn.dataset.dcPick);
            this.pick(payload);
          } catch (err) {
            console.error('[DivanConfigurator] pick payload bad:', err);
          }
          return;
        }

        // Removal panel: toggle, step, done
        const toggleBtn = e.target.closest('[data-dc-removal-toggle]');
        if (toggleBtn && this.panelEl && this.panelEl.contains(toggleBtn)) {
          e.preventDefault();
          this.toggleRemovalItem(toggleBtn.closest('[data-dc-removal-item]'));
          return;
        }
        const stepBtn = e.target.closest('[data-dc-removal-step]');
        if (stepBtn && this.panelEl && this.panelEl.contains(stepBtn)) {
          e.preventDefault();
          this.stepRemovalQty(stepBtn.closest('[data-dc-removal-item]'), parseInt(stepBtn.dataset.delta, 10));
          return;
        }
        if (e.target.closest('[data-dc-removal-done]')) {
          e.preventDefault();
          this.commitRemoval();
          this.closePanel();
          return;
        }
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.panelEl && !this.panelEl.hidden) this.closePanel();
      });
    }

    /* ---------- Panel rendering ---------- */
    openPanel(key) {
      if (!this.panelEl) return;

      let html = '';
      let title = key;

      if (key === 'size') {
        title = 'Select Size';
        const selectedId = this.state.sizeSelected ? this.state.sizeVariantId : null;
        html = this.renderVariantList(this.data.main.variants, selectedId, 'size');
      } else if (key === 'removal' && this.data.options && this.data.options.removal) {
        // Multi-quantity panel: customer can pick several items at once
        title = this.data.options.removal.label;
        const selected = (this.state.selections.removal && this.state.selections.removal.byId) || {};
        html = this.renderRemovalList(this.data.options.removal.variants, selected);
      } else if (key === 'storage' && this.data.options && this.data.options.storage) {
        // Storage is per-size: `bySizeIndex[i]` is the storage product for
        // variant i of the divan. Find the current variant's index and use it.
        title = this.data.options.storage.label;
        const sizeIndex = this.data.main.variants.findIndex(v => v.id === this.state.sizeVariantId);
        const list = this.data.options.storage.bySizeIndex || [];
        const forSize = sizeIndex >= 0 ? list[sizeIndex] : null;
        if (!forSize) {
          html = '<p>No storage options available for this size.</p>';
        } else {
          const selectedId = this.state.selections.storage?.variantId ?? null;
          const filtered = this.filterVariantsForSize('storage', forSize.variants);
          html = this.renderAddonList(filtered, selectedId, 'storage');
        }
      } else if (this.data.options && this.data.options[key]) {
        const opt = this.data.options[key];
        title = opt.label;
        const selectedId = this.state.selections[key]?.variantId ?? null;
        const filtered = this.filterVariantsForSize(key, opt.variants);
        html = this.renderAddonList(filtered, selectedId, key);
      } else if (key === 'fabric') {
        title = 'Fabric';
        html = this.renderSwatchList(this.data.fabrics, this.state.selections.fabric?.name, 'fabric');
      } else if (key === 'headboard') {
        title = 'Headboard';
        html = this.renderSwatchList(this.data.headboards, this.state.selections.headboard?.name, 'headboard');
      } else {
        return;
      }

      this.panelTitle.textContent = title;
      this.panelBody.innerHTML = html;
      this.panelEl.hidden = false;
      document.body.style.overflow = 'hidden';
    }

    closePanel() {
      if (!this.panelEl) return;
      this.panelEl.hidden = true;
      this.panelBody.innerHTML = '';
      document.body.style.overflow = '';
    }

    filterVariantsForSize(key, variants) {
      if (key !== 'storage' || !this.state.sizeSelected) return variants;
      const sizeName = this.currentSizeName();
      const allowed = (this.data.sizeRestrictions || {})[sizeName];
      if (!allowed) return variants;
      const set = new Set(allowed);
      return variants.filter((v) => set.has(v.title));
    }

    currentSizeName() {
      const v = this.data.main.variants.find((x) => x.id === this.state.sizeVariantId);
      return v ? v.title : '';
    }

    renderVariantList(variants, selectedId, key) {
      // Size variant cards: name + dimensions + price + stock
      const dims = this.data.sizeDimensions || {};
      return variants.map((v) => {
        const isSelected = v.id === selectedId;
        const dim = dims[v.title] || '';
        const price = this.money(v.price);
        const stockClass = v.available ? 'dc__size-stock--in' : 'dc__size-stock--out';
        const stockText = v.available ? 'In Stock' : 'Sold Out';
        return `
          <button type="button" class="dc__size-card" aria-pressed="${isSelected}"
                  data-dc-pick='${this.escAttr(JSON.stringify({ key, variantId: v.id, label: v.title, price: v.price }))}'>
            <span class="dc__size-radio" aria-hidden="true"></span>
            <span class="dc__size-info">
              <span class="dc__size-name">${this.esc(v.title)}</span>
              ${dim ? `<span class="dc__size-dim">${this.esc(dim)}</span>` : ''}
            </span>
            <span class="dc__size-meta">
              <span class="dc__size-price">${price}</span>
              <span class="dc__size-stock ${stockClass}">${stockText}</span>
            </span>
          </button>
        `;
      }).join('');
    }

    renderAddonList(variants, selectedId, key) {
      if (!variants.length) return '<p>No options available for this size.</p>';
      return variants.map((v) => {
        const isFree = v.price === 0;
        const priceLabel = isFree ? 'Included' : `+ ${this.money(v.price)}`;
        return `
          <button type="button" class="dc__opt" aria-pressed="${v.id === selectedId}"
                  data-dc-pick='${this.escAttr(JSON.stringify({ key, variantId: v.id, label: v.title, price: v.price, thumb: v.image }))}'>
            ${v.image ? `<span class="dc__opt-thumb"><img src="${v.image}" alt="" loading="lazy"></span>` : ''}
            <span class="dc__opt-text">${this.esc(v.title)}</span>
            <span class="dc__opt-price ${isFree ? 'dc__opt-price--free' : ''}">${priceLabel}</span>
          </button>
        `;
      }).join('');
    }

    renderRemovalList(variants, selectedById) {
      const rows = variants.map((v) => {
        const sel = selectedById[v.id];
        const checked = !!sel;
        const qty = sel ? sel.qty : 1;
        return `
          <div class="dc__removal-row ${checked ? 'is-selected' : ''}" data-dc-removal-item data-variant-id="${v.id}" data-name="${this.escAttr(v.title)}" data-price="${v.price}">
            <button type="button" class="dc__removal-check" data-dc-removal-toggle aria-pressed="${checked}">${checked ? '✓' : ''}</button>
            <span class="dc__removal-name">${this.esc(v.title)}</span>
            <span class="dc__removal-price">+ ${this.money(v.price)}</span>
            <div class="dc__removal-stepper" ${checked ? '' : 'hidden'}>
              <button type="button" class="dc__removal-step" data-dc-removal-step data-delta="-1" ${qty <= 1 ? 'disabled' : ''}>−</button>
              <input type="text" class="dc__removal-qty" value="${qty}" readonly data-dc-removal-qty aria-label="Quantity for ${this.esc(v.title)}">
              <button type="button" class="dc__removal-step" data-dc-removal-step data-delta="1">+</button>
            </div>
          </div>
        `;
      }).join('');
      return rows + '<button type="button" class="dc__removal-done" data-dc-removal-done>Done</button>';
    }

    renderSwatchList(items, selectedName, key) {
      if (!items || !items.length) return '<p>No options available.</p>';
      const groups = {};
      items.forEach((it) => {
        const k = (key === 'fabric' && it.series) ? it.series : '';
        (groups[k] = groups[k] || []).push(it);
      });
      return Object.entries(groups).map(([series, list]) => `
        <div class="dc__swatch-series">
          ${series ? `<h3 class="dc__swatch-series-title">${this.esc(series)}</h3>` : ''}
          <div class="dc__swatch-grid">
            ${list.map((it) => {
              const surchargeLabel = it.surcharge > 0 ? ` (+${this.money(it.surcharge)})` : '';
              return `
                <button type="button" class="dc__swatch" aria-pressed="${it.name === selectedName}"
                        data-dc-pick='${this.escAttr(JSON.stringify({ key, name: it.name, surcharge: it.surcharge, thumb: it.image }))}'>
                  <img class="dc__swatch-img" src="${it.image}" alt="${this.esc(it.name)}" loading="lazy">
                  <span class="dc__swatch-name">${this.esc(it.name)}${surchargeLabel}</span>
                </button>
              `;
            }).join('')}
          </div>
        </div>
      `).join('');
    }

    /* ---------- Selection ---------- */
    pick(payload) {
      const { key } = payload;

      if (key === 'size') {
        const changed = this.state.sizeVariantId !== payload.variantId;
        this.state.sizeVariantId = payload.variantId;
        this.state.sizeSelected = true;
        this.setRowValue('size', `- ${payload.label}`);

        // Storage is now size-specific (different Shopify product per size).
        // Any previously-selected storage variant belongs to the old size's
        // product, so it MUST be cleared when the size changes — the price
        // and the variant ID will both be wrong otherwise.
        if (changed && this.state.selections.storage) {
          this.clearSelection('storage');
        }
      } else if (this.data.options && this.data.options[key] && key !== 'removal') {
        // Single-select option (storage, headboard_type)
        this.state.selections[key] = {
          variantId: payload.variantId,
          label: payload.label,
          price: payload.price,
          thumb: payload.thumb,
        };
        const priceSuffix = payload.price > 0 ? ` +${this.money(payload.price)}` : '';
        this.setRowValue(key, `- ${payload.label}${priceSuffix}`);
        this.setRowThumb(key, payload.thumb);
      } else if (key === 'fabric' || key === 'headboard') {
        this.state.selections[key] = {
          name: payload.name,
          surcharge: payload.surcharge || 0,
          thumb: payload.thumb,
        };
        const priceSuffix = (payload.surcharge && payload.surcharge > 0) ? ` +${this.money(payload.surcharge)}` : '';
        this.setRowValue(key, `- ${payload.name}${priceSuffix}`);
        this.setRowThumb(key, payload.thumb);
        if (key === 'headboard') {
          // "Not Required" should NOT reveal Headboard Type — treat it as
          // no headboard for visibility purposes and clear any prior type.
          const noHeadboard = /^not required$/i.test(payload.name || '');
          this.state.headboardSelected = !noHeadboard;
          if (noHeadboard) this.clearSelection('headboard_type');
        }
      }

      this.refreshTotal();
      this.updateUiState();
      this.closePanel();
    }

    clearSelection(key) {
      this.state.selections[key] = null;
      this.setRowValue(key, '');
      this.setRowThumb(key, null);
      if (key === 'headboard') this.state.headboardSelected = false;
    }

    /* ---------- Removal multi-select panel helpers ---------- */
    toggleRemovalItem(row) {
      if (!row) return;
      const isSelected = row.classList.toggle('is-selected');
      const stepper = row.querySelector('.dc__removal-stepper');
      const toggleBtn = row.querySelector('[data-dc-removal-toggle]');
      const qtyInput = row.querySelector('[data-dc-removal-qty]');
      if (stepper) stepper.hidden = !isSelected;
      if (toggleBtn) {
        toggleBtn.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
        toggleBtn.textContent = isSelected ? '✓' : '';
      }
      if (isSelected && qtyInput && (!qtyInput.value || qtyInput.value === '0')) qtyInput.value = '1';
    }

    stepRemovalQty(row, delta) {
      if (!row) return;
      const qtyInput = row.querySelector('[data-dc-removal-qty]');
      if (!qtyInput) return;
      let next = (parseInt(qtyInput.value, 10) || 0) + delta;
      if (next < 1) next = 1;
      if (next > 9) next = 9;
      qtyInput.value = next;
      // Disable minus when at 1
      const minus = row.querySelector('[data-delta="-1"]');
      if (minus) minus.disabled = next <= 1;
    }

    /**
     * Read the panel's checked items + quantities and persist to state, then
     * update the row label / refresh the total.
     */
    commitRemoval() {
      if (!this.panelEl) return;
      const rows = this.panelEl.querySelectorAll('[data-dc-removal-item]');
      const byId = {};
      let totalCount = 0;
      let totalCost = 0;
      const parts = [];
      rows.forEach((row) => {
        if (!row.classList.contains('is-selected')) return;
        const id = Number(row.dataset.variantId);
        const name = row.dataset.name;
        const price = Number(row.dataset.price);
        const qtyInput = row.querySelector('[data-dc-removal-qty]');
        const qty = Math.max(1, parseInt(qtyInput && qtyInput.value || '1', 10) || 1);
        byId[id] = { variantId: id, name, price, qty };
        totalCount += qty;
        totalCost += price * qty;
        parts.push(qty > 1 ? `${name} × ${qty}` : name);
      });

      if (Object.keys(byId).length === 0) {
        this.state.selections.removal = null;
        this.setRowValue('removal', '');
      } else {
        this.state.selections.removal = { byId };
        const label = `- ${parts.join(', ')}  +${this.money(totalCost)}`;
        this.setRowValue('removal', label);
      }
      this.refreshTotal();
    }

    setRowValue(key, value) {
      const el = this.querySelector(`[data-dc-value="${key}"]`);
      if (el) el.textContent = value;
      // Toggle is-selected on the row button so CSS can switch to the
      // expanded layout (thumb + value visible).
      const row = this.querySelector(`[data-dc-open="${key}"]`);
      if (row) row.classList.toggle('is-selected', !!(value && value.trim()));
    }
    setRowThumb(key, src) {
      const wrap = this.querySelector(`[data-dc-thumb="${key}"]`);
      if (!wrap) return;
      if (!src) { wrap.hidden = true; return; }
      const img = wrap.querySelector('img');
      img.src = src;
      wrap.hidden = false;
    }

    updateUiState() {
      this.setAttribute('data-size-selected', this.state.sizeSelected ? 'true' : 'false');
      this.setAttribute('data-headboard-selected', this.state.headboardSelected ? 'true' : 'false');
    }

    /* ---------- Pricing ---------- */
    refreshTotal() {
      const totalEl = this.querySelector('[data-dc-total]');
      const compareEl = this.querySelector('[data-dc-compare]');
      const saveEl = this.querySelector('[data-dc-save]');

      // If no size picked yet, show the base "from" price (min variant)
      const variant = this.data.main.variants.find((v) => v.id === this.state.sizeVariantId);
      let basePrice;
      let baseCompare;
      if (variant) {
        basePrice = variant.price;
        baseCompare = variant.compareAt || 0;
      } else {
        // use min variant as fallback so user sees a sensible price
        const minVariant = this.data.main.variants.reduce((min, v) => v.price < (min?.price ?? Infinity) ? v : min, null);
        basePrice = minVariant ? minVariant.price : 0;
        baseCompare = minVariant ? (minVariant.compareAt || 0) : 0;
      }

      let total = basePrice;
      Object.values(this.state.selections).forEach((sel) => {
        if (!sel) return;
        if (sel.byId) {
          // multi-quantity (removal)
          Object.values(sel.byId).forEach((it) => { total += (it.price || 0) * (it.qty || 0); });
        } else if (sel.variantId) {
          total += sel.price;
        } else {
          total += sel.surcharge || 0;
        }
      });

      if (totalEl) totalEl.textContent = this.money(total);
      if (baseCompare > basePrice) {
        const compareAdjusted = baseCompare + (total - basePrice);
        if (compareEl) { compareEl.textContent = this.money(compareAdjusted); compareEl.hidden = false; }
        if (saveEl) { saveEl.textContent = `save ${this.money(compareAdjusted - total)}`; saveEl.hidden = false; }
      } else {
        if (compareEl) compareEl.hidden = true;
        if (saveEl) saveEl.hidden = true;
      }
    }

    /* ---------- Add to cart ---------- */
    async submit() {
      const atc = this.querySelector('[data-dc-atc]');
      if (!atc) return;
      const labelEl = atc.querySelector('.dc__atc-label');
      const originalLabel = labelEl ? labelEl.textContent : '';
      atc.setAttribute('disabled', '');
      if (labelEl) labelEl.textContent = 'Adding…';

      const bundleId = this.makeBundleId();
      const items = [];

      // Qty selector multiplies every line in the bundle in lockstep.
      const qtySelect = this.querySelector('[data-dc-qty]');
      const qty = Math.max(1, parseInt(qtySelect && qtySelect.value || '1', 10) || 1);

      // Main item carries cosmetic + delivery as line item properties
      const mainProps = { _bundle_id: bundleId };
      if (this.state.selections.fabric)    mainProps['Fabric']    = this.state.selections.fabric.name;
      if (this.state.selections.headboard) mainProps['Headboard'] = this.state.selections.headboard.name;

      const postcodeInput = document.querySelector('#postal-code-input');
      if (postcodeInput && postcodeInput.value.trim()) {
        mainProps['Postal Code'] = postcodeInput.value.trim().toUpperCase();
      }
      const deliveryDate = document.querySelector('#delivery-date-select');
      if (deliveryDate && deliveryDate.value) {
        mainProps['Delivery Date'] = deliveryDate.value;
      }

      items.push({ id: this.state.sizeVariantId, quantity: qty, properties: mainProps });

      ['storage', 'headboard_type'].forEach((key) => {
        const sel = this.state.selections[key];
        if (sel && sel.variantId && sel.price > 0) {
          items.push({
            id: sel.variantId,
            quantity: qty,
            properties: { _bundle_id: bundleId, _bundle_role: key },
          });
        }
      });

      // Removal — multi-item: one cart line per selected item, qty multiplied
      const removal = this.state.selections.removal;
      if (removal && removal.byId) {
        Object.values(removal.byId).forEach((it) => {
          if (it.variantId && it.price > 0 && it.qty > 0) {
            items.push({
              id: it.variantId,
              quantity: qty * it.qty,
              properties: { _bundle_id: bundleId, _bundle_role: 'removal' },
            });
          }
        });
      }

      try {
        // Ask the cart-drawer element which sections it needs so we request
        // them inline with /cart/add.js — that lets renderContents replace
        // the drawer's HTML without an extra round-trip and avoids the
        // empty-state flash.
        const cartDrawer = document.querySelector('cart-drawer');
        let sections = [];
        if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
          sections = cartDrawer.getSectionsToRender().map((s) => s.section);
        }

        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items, sections: [...new Set(sections)], sections_url: window.location.pathname }),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.description || `HTTP ${res.status}`);
        }
        const data = await res.json();
        if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
          cartDrawer.renderContents(data, true);
        } else {
          document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));
          document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
        }
        atc.removeAttribute('disabled');
        if (labelEl) labelEl.textContent = originalLabel;
      } catch (err) {
        this.showError(err.message || 'Could not add to cart.');
        atc.removeAttribute('disabled');
        if (labelEl) labelEl.textContent = originalLabel;
      }
    }

    showError(msg) {
      let box = this.querySelector('.dc__error');
      if (!box) {
        box = document.createElement('div');
        box.className = 'dc__error';
        const atc = this.querySelector('[data-dc-atc]');
        if (atc) atc.before(box);
        else this.appendChild(box);
      }
      box.textContent = msg;
    }

    /* ---------- Helpers ---------- */
    makeBundleId() {
      return 'b_' + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
    }

    money(cents) {
      const v = cents / 100;
      const whole = Math.round(v) === v;
      const fmt = whole ? v.toFixed(0) : v.toFixed(2);
      const sym = (this.data.moneyFormat || '£{{amount}}').match(/[^\d{}]+/)?.[0] || '£';
      return `${sym}${fmt.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
    }

    esc(s) {
      return String(s ?? '').replace(/[&<>"']/g, (c) => ({
        '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'
      }[c]));
    }
    escAttr(s) { return this.esc(s); }
  }

  if (!customElements.get('divan-configurator')) {
    customElements.define('divan-configurator', DivanConfigurator);
  }
})();
